import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { loadPrefs, subscribePrefs, type Prefs } from './prefs';
import type { PeerAudioState } from './spatial/SpatialMixer';
import type { VoiceController, VoiceSnapshot } from './voice/VoiceController';

export function useVoice(c: VoiceController): VoiceSnapshot {
  return useSyncExternalStore(c.subscribe, c.getSnapshot);
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(subscribePrefs, loadPrefs);
}

/** Per-peer audio state (distance, gain) sampled at `hz`. */
export function usePeerAudio(c: VoiceController, hz = 5): Map<string, PeerAudioState> {
  const [map, setMap] = useState<Map<string, PeerAudioState>>(new Map());
  useEffect(() => {
    const tick = () => setMap(new Map(c.getPeerAudio().map((p) => [p.identity, p])));
    tick();
    const id = setInterval(tick, 1000 / hz);
    return () => clearInterval(id);
  }, [c, hz]);
  return map;
}

/** Listener yaw sampled at `hz` (for the radar compass). */
export function useListenerYaw(c: VoiceController, hz = 10): number {
  const [yaw, setYaw] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setYaw(c.getMixer()?.getListenerYaw() ?? 0), 1000 / hz);
    return () => clearInterval(id);
  }, [c, hz]);
  return yaw;
}

/** Calls `fn` on every animation frame while mounted (paused by the browser in background tabs). */
export function useAnimationFrame(fn: () => void): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    let id = 0;
    const loop = () => {
      ref.current();
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);
}

export interface DeviceLists {
  inputs: MediaDeviceInfo[];
  outputs: MediaDeviceInfo[];
  outputSupported: boolean;
}

export function useDevices(refreshKey: unknown = 0): DeviceLists {
  const [lists, setLists] = useState<DeviceLists>({ inputs: [], outputs: [], outputSupported: false });
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.enumerateDevices) return;
    let alive = true;
    const load = async () => {
      try {
        const all = await md.enumerateDevices();
        if (!alive) return;
        setLists({
          inputs: all.filter((d) => d.kind === 'audioinput' && d.deviceId),
          outputs: all.filter((d) => d.kind === 'audiooutput' && d.deviceId),
          // Le voci escono da un elemento <audio> (anti-eco): conta il suo setSinkId.
          outputSupported: typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype,
        });
      } catch {
        /* ignore */
      }
    };
    void load();
    md.addEventListener?.('devicechange', load);
    return () => {
      alive = false;
      md.removeEventListener?.('devicechange', load);
    };
  }, [refreshKey]);
  return lists;
}

export function useDocumentHidden(): boolean {
  return useSyncExternalStore(
    (l) => {
      document.addEventListener('visibilitychange', l);
      return () => document.removeEventListener('visibilitychange', l);
    },
    () => document.hidden,
  );
}
